import { useRef } from 'react';
import { useBrandId } from 'AppProvider';
import useSearchQueryParams from 'hooks/use-search-params';
import useBrandCategories from 'api/categories/useBrandCategories';
import useBrandVibes from 'api/vibes/useBrandVibes';

/** categoryIds/vibeIds may arrive as an array (embed state) or a CSV string (deep link). */
const toIdArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value as string[];
  if (typeof value === 'string' && value) return value.split(',');
  return [];
};

export interface ScheduleFilterOption {
  /** Encoded as `cat:<id>` or `vibe:<id>` so a single dropdown can drive either URL param. */
  readonly value: string;
  readonly label: string;
}

export interface ScheduleFilter {
  /** Whether the combined filter dropdown should render at all. */
  readonly enabled: boolean;
  readonly options: ReadonlyArray<ScheduleFilterOption>;
  /** Currently-selected `cat:<id>` / `vibe:<id>` values; empty array = "All experiences". */
  readonly selectedValues: ReadonlyArray<string>;
  /** Display name when exactly one filter is active (drives the empty-state copy). */
  readonly selectedLabel: string | null;
  readonly onChange: (values: ReadonlyArray<string>) => void;
}

/**
 * The schedule view's combined Category / Vibe filter. Categories and (when enabled) vibes are
 * merged into one multi-select dropdown; selections drive `?categoryIds=` AND/OR `?vibeIds=` in
 * the URL so they deep-link and persist across date changes. Both axes can be active at once.
 */
const useScheduleFilter = (opts: {
  readonly categoryEnabled: boolean;
  readonly vibeEnabled: boolean;
}): ScheduleFilter => {
  const brandId = useBrandId();
  const { categories } = useBrandCategories(brandId);
  const { vibes } = useBrandVibes();
  const { searchParams, replaceParams } = useSearchQueryParams();

  const cats = opts.categoryEnabled ? categories ?? [] : [];
  const vbs = opts.vibeEnabled ? vibes ?? [] : [];

  const options: ScheduleFilterOption[] = [
    ...cats.map((c) => ({ value: `cat:${c.id}`, label: c.name })),
    ...vbs.map((v) => ({ value: `vibe:${v.id}`, label: v.name })),
  ];

  const selectedCategoryIds = toIdArray(searchParams.categoryIds);
  const selectedVibeIds = toIdArray(searchParams.vibeIds);
  const selectedValues: string[] = [
    ...selectedCategoryIds.map((id) => `cat:${id}`),
    ...selectedVibeIds.map((id) => `vibe:${id}`),
  ];

  // Resolve a single display label only when exactly one filter is active — used by the empty
  // state copy ("No {Category} experiences scheduled"). Cache the last resolved label so a
  // transient empty options list (while categories/vibes revalidate) doesn't flash the copy
  // back to the generic form.
  const singleSelected = selectedValues.length === 1 ? selectedValues[0] : null;
  const lastLabelRef = useRef<{ value: string; label: string } | null>(null);
  const resolvedLabel = singleSelected
    ? options.find((option) => option.value === singleSelected)?.label ?? null
    : null;
  if (singleSelected && resolvedLabel) {
    lastLabelRef.current = { value: singleSelected, label: resolvedLabel };
  }
  const selectedLabel =
    resolvedLabel ??
    (singleSelected && lastLabelRef.current?.value === singleSelected
      ? lastLabelRef.current.label
      : null);

  const onChange = (values: ReadonlyArray<string>): void => {
    const nextCategoryIds: string[] = [];
    const nextVibeIds: string[] = [];
    values.forEach((value) => {
      const [kind, id] = value.split(':');
      if (!id) return;
      if (kind === 'cat') nextCategoryIds.push(id);
      else if (kind === 'vibe') nextVibeIds.push(id);
    });
    const next: Record<string, unknown> = { ...searchParams };
    if (nextCategoryIds.length > 0) next.categoryIds = nextCategoryIds;
    else delete next.categoryIds;
    if (nextVibeIds.length > 0) next.vibeIds = nextVibeIds;
    else delete next.vibeIds;
    replaceParams(next);
  };

  return {
    enabled: options.length > 0,
    options,
    selectedValues,
    selectedLabel,
    onChange,
  };
};

export default useScheduleFilter;
